import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ColumnsState,
  Column,
  ColumnsListData,
} from '../../types/columns.types';

const initialState: ColumnsState = {
  columns: [],
  selectedColumn: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
};

const columnsSlice = createSlice({
  name: 'columns',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setColumns: (state, action: PayloadAction<ColumnsListData>) => {
      state.columns = action.payload.columns;
      state.pagination = {
        ...state.pagination,
        totalPages: action.payload.totalPages,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
      state.error = null;
    },
    setSelectedColumn: (state, action: PayloadAction<Column | null>) => {
      state.selectedColumn = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedColumn: (state) => {
      state.selectedColumn = null;
    },
    addColumn: (state, action: PayloadAction<Column>) => {
      state.columns.unshift(action.payload);
      state.pagination.totalRecords += 1;
    },
    updateColumn: (state, action: PayloadAction<Column>) => {
      const index = state.columns.findIndex((col) => col.id === action.payload.id);
      if (index !== -1) {
        state.columns[index] = action.payload;
      }
      if (state.selectedColumn?.id === action.payload.id) {
        state.selectedColumn = action.payload;
      }
    },
    deleteColumn: (state, action: PayloadAction<string>) => {
      state.columns = state.columns.filter((col) => col.id !== action.payload);
      state.pagination.totalRecords -= 1;
      if (state.selectedColumn?.id === action.payload) {
        state.selectedColumn = null;
      }
    },
    resetColumns: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setColumns,
  setSelectedColumn,
  setPage,
  setPageSize,
  setSearch,
  clearError,
  clearSelectedColumn,
  addColumn,
  updateColumn,
  deleteColumn,
  resetColumns,
} = columnsSlice.actions;

export default columnsSlice.reducer;
